<template>
  <div
    ref="dropdownRef"
    class="searchable-dropdown"
    :class="{ 'is-active': isOpen, 'is-dark': dark }"
    @keydown="handleKeydown"
  >
    <div class="select is-fullwidth no-arrow">
      <button
        type="button"
        class="select-button"
        @click.prevent="toggleDropdown"
        :disabled="disabled"
      >
        <div class="is-flex is-align-items-center is-flex-grow-1" :class="{ 'has-text-truncate': truncate }">
          <span
            v-if="showIcons && selectedItemIcon"
            class="icon mr-2"
            :style="selectedItemColor ? { color: selectedItemColor } : ''"
            :class="{ 'has-text-primary': !selectedItemColor }"
          >
            <font-awesome-icon :icon="[selectedItemIconStyle || iconStyle, selectedItemIcon]" />
          </span>
          <span :class="{ 'has-text-truncate': truncate }">{{ selectedItemText || effectivePlaceholder }}</span>
        </div>
        <span class="icon is-small">
          <font-awesome-icon :icon="['fas', 'chevron-down']" size="xs" />
        </span>
      </button>
    </div>

    <div class="select-menu" v-show="isOpen">
      <div class="select-content">
        <a
          v-if="showAllOption"
          class="list-item"
          :class="{ 'is-highlighted': highlightedIndex === 0 }"
          @click.stop.prevent="selectItem(null)"
        >
          <label class="is-flex is-align-items-center checkbox-label">
            <input
              v-if="multiple"
              type="checkbox"
              class="mr-2"
              :checked="isAllSelected"
              @click.stop="selectItem(null)"
            />
            <span>{{ allLabel }}</span>
          </label>
        </a>
        <hr v-if="showAllOption" class="divider" />
        <div v-if="!hideSearch && searchable" class="search-item">
          <p class="control has-icons-left">
            <input
              type="text"
              class="input"
              ref="searchInputRef"
              :placeholder="effectiveSearchPlaceholder"
              v-model="searchQuery"
              @click.stop
            />
            <span class="icon is-small is-left">
              <font-awesome-icon :icon="['fas', 'search']" />
            </span>
          </p>
        </div>

        <template v-for="(group, groupName) in groupedItems" :key="groupName">
          <div v-if="group.length > 0" class="list-section">
            <div
              v-if="showGroupHeaders && shouldShowGrouping"
              class="list-header"
            >
              <h6 class="is-size-7 has-text-grey has-text-weight-bold">
                {{ getGroupLabel(groupName) }}
              </h6>
            </div>
            <a
              v-for="item in group"
              :key="item[valueField]"
              class="list-item"
              :class="{
                'has-text-grey': isItemInactive(item),
                'is-selected': selectedLookup[item[valueField]] && !multiple,
                'is-highlighted': navIndexByValue[item[valueField]] === highlightedIndex,
              }"
              @click.stop.prevent="selectItem(item)"
            >
              <!-- Use slot if provided, otherwise use default display -->
              <slot name="item" :item="item">
                <!-- Default display (backward compatible) -->
                <label class="is-flex is-align-items-center checkbox-label" :class="{ 'has-text-truncate': truncate }">
                  <input
                    v-if="multiple"
                    type="checkbox"
                    class="mr-2"
                    :checked="selectedLookup[item[valueField]]"
                    @click.stop="selectItem(item)"
                  />
                  <span
                    v-if="showIcons && item[iconField]"
                    class="icon mr-2"
                    :style="item.color ? { color: item.color } : ''"
                    :class="{ 'has-text-primary': !item.color }"
                  >
                    <font-awesome-icon :icon="[item.iconStyle || iconStyle, item[iconField]]" />
                  </span>
                  <span :class="{ 'has-text-truncate': truncate }">{{ item[displayField] }}</span>
                  <span
                    v-if="selectedLookup[item[valueField]] && !multiple"
                    class="icon ml-auto has-text-primary"
                  >
                    <font-awesome-icon :icon="['fas', 'check']" />
                  </span>
                </label>
              </slot>
            </a>
            <hr
              v-if="shouldShowGrouping && !isLastGroup(groupName)"
              class="divider"
            />
          </div>
        </template>

        <a
          v-if="creatableOption"
          class="list-item creatable-item"
          :class="{ 'is-highlighted': highlightedIndex === creatableNavIndex }"
          @click.stop.prevent="selectCreatable"
        >
          <label class="is-flex is-align-items-center checkbox-label">
            <span class="icon mr-2 has-text-primary">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </span>
            <span>Create "<strong>{{ creatableOption }}</strong>"</span>
          </label>
        </a>

        <div v-if="noItemsFound" class="list-item">
          <p class="has-text-grey">{{ noResultsText }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

const props = defineProps({
  // Core data props
  items: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: [String, Number, Array],
    default: "",
  },
  valueField: {
    type: String,
    default: "id",
  },
  displayField: {
    type: String,
    default: "name",
  },

  // Multi-select prop
  multiple: {
    type: Boolean,
    default: false,
  },

  // What the "All" row does in multi-select mode.
  //
  // Default (false) keeps the long-standing filter meaning: All = no filter, so
  // choosing it CLEARS the selection. Filters across the app rely on that.
  //
  // Set true where the control assigns things rather than filters them, and All
  // should tick every option instead.
  allSelectsAll: {
    type: Boolean,
    default: false,
  },

  // Grouping props
  groupBy: {
    type: String,
    default: null,
  },
  groupLabels: {
    type: Object,
    default: () => ({}),
  },
  showGroupHeaders: {
    type: Boolean,
    default: true,
  },

  // Text/label props
  placeholder: {
    type: String,
    default: "Select an item",
  },
  searchPlaceholder: {
    type: String,
    default: "Search...",
  },
  allLabel: {
    type: String,
    default: "All Items",
  },
  noResultsText: {
    type: String,
    default: "No items found",
  },

  // Behavior props
  showAllOption: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideSearch: {
    type: Boolean,
    default: false,
  },
  showIcons: {
    type: Boolean,
    default: false,
  },
  iconField: {
    type: String,
    default: "icon",
  },
  iconStyle: {
    type: String,
    default: "fal",
    validator: (value) => ["fas", "fal", "far", "fab"].includes(value),
  },

  // Optional function to determine if an item should be displayed as inactive
  isInactive: {
    type: Function,
    default: () => false,
  },
  
  // Truncate text in dropdown and button
  truncate: {
    type: Boolean,
    default: false,
  },
  
  // Hide search even when searchable is true
  searchable: {
    type: Boolean,
    default: true,
  },

  // Dark mode styling (for use on dark backgrounds like sidebar)
  dark: {
    type: Boolean,
    default: false,
  },

  // Allow typing a new value not in the list
  creatable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "select", "open"]);

// Derive effective placeholder text — creatable selects hint that typing creates new items
const effectivePlaceholder = computed(() => {
  if (props.placeholder !== "Select an item") return props.placeholder;
  return props.creatable ? "Type to search or create" : props.placeholder;
});
const effectiveSearchPlaceholder = computed(() => {
  if (props.searchPlaceholder !== "Search...") return props.searchPlaceholder;
  return props.creatable ? "Type to search or create..." : props.searchPlaceholder;
});

const isOpen = ref(false);
const searchQuery = ref("");
const dropdownRef = ref(null);
const searchInputRef = ref(null);
// Keyboard navigation: index into navigableItems, -1 = nothing highlighted
const highlightedIndex = ref(-1);

// Auto-focus the search input whenever the dropdown opens so users can type immediately.
// Watching isOpen (instead of hooking toggleDropdown) covers every open path,
// including parents flipping the exposed isOpen ref directly.
watch(isOpen, async (open) => {
  highlightedIndex.value = -1;
  if (open && !props.hideSearch && props.searchable) {
    await nextTick(); // wait for v-show to reveal the menu before focusing
    searchInputRef.value?.focus();
  }
});

// Typing changes the result set, so any previous highlight position is stale
watch(searchQuery, () => {
  highlightedIndex.value = -1;
});

// Keep the highlighted row visible while arrowing through a long list
watch(highlightedIndex, async (idx) => {
  if (idx < 0) return;
  await nextTick();
  const el = dropdownRef.value?.querySelector(".list-item.is-highlighted");
  el?.scrollIntoView({ block: "nearest" });
});

// Expose methods for parent components to control dropdown
const close = () => {
  isOpen.value = false;
};

defineExpose({ close, isOpen });

// Close dropdown when clicking outside THIS specific dropdown
const closeDropdown = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    isOpen.value = false;
  }
};

const toggleDropdown = () => {
  if (props.disabled) return;
  const wasOpen = isOpen.value;
  isOpen.value = !isOpen.value;

  // Emit open event when dropdown opens so parent can coordinate multiple dropdowns
  if (!wasOpen && isOpen.value) {
    emit("open");
  }
};

onMounted(() => {
  document.addEventListener("click", closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener("click", closeDropdown);
});

const filteredItems = computed(() => {
  if (!searchQuery.value) return props.items;

  return props.items.filter((item) =>
    item[props.displayField]
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase())
  );
});

const groupedItems = computed(() => {
  if (!props.groupBy) {
    // If no groupBy is provided, put all items in a single group
    return { default: filteredItems.value };
  }

  // Group items by the specified property
  return filteredItems.value.reduce((groups, item) => {
    const groupKey = item[props.groupBy];
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {});
});

const groupKeys = computed(() => {
  return Object.keys(groupedItems.value);
});

// Determine if we should show grouping elements (headers/separators)
const shouldShowGrouping = computed(() => {
  return props.groupBy && groupKeys.value.length > 1;
});

const isLastGroup = (groupName) => {
  const keys = groupKeys.value;
  return keys.indexOf(groupName) === keys.length - 1;
};

const getGroupLabel = (groupKey) => {
  return props.groupLabels[groupKey] || `Group: ${groupKey}`;
};

const isItemInactive = (item) => {
  return props.isInactive(item);
};

const noItemsFound = computed(() => {
  if (props.creatable && searchQuery.value.trim()) return false;
  return Object.values(groupedItems.value).every((group) => group.length === 0);
});

// Creatable: show "Create X" when search doesn't exactly match any item
const creatableOption = computed(() => {
  if (!props.creatable || !searchQuery.value.trim()) return null;
  const query = searchQuery.value.trim();
  const exactMatch = props.items.some(
    item => item[props.displayField]?.toLowerCase() === query.toLowerCase()
  );
  return exactMatch ? null : query;
});

// Multi-select helper - computed lookup object for reactive checkbox state
const selectedLookup = computed(() => {
  const lookup = {};
  if (!props.multiple) {
    lookup[props.modelValue] = true;
  } else {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [];
    values.forEach(v => {
      lookup[v] = true;
    });
  }
  return lookup;
});

const selectableValues = computed(() =>
  props.items.filter((i) => !i?.disabled).map((i) => i[props.valueField])
);

const isAllSelected = computed(() => {
  if (!props.multiple) {
    return !props.modelValue || props.modelValue === "";
  }
  const selectedValues = Array.isArray(props.modelValue)
    ? props.modelValue
    : [];
  // In assign mode the box is ticked when everything is selected; in filter
  // mode it is ticked when nothing is (no filter applied).
  if (props.allSelectsAll) {
    return (
      selectableValues.value.length > 0 &&
      selectableValues.value.every((v) => selectedValues.includes(v))
    );
  }
  return selectedValues.length === 0;
});

const selectedItemText = computed(() => {
  if (props.multiple) {
    const selectedValues = Array.isArray(props.modelValue)
      ? props.modelValue
      : [];
    if (selectedValues.length === 0) {
      return "";
    }

    const selectedItems = props.items.filter((item) =>
      selectedValues.includes(item[props.valueField])
    );

    if (selectedItems.length === 1) {
      return selectedItems[0][props.displayField];
    } else {
      return `${selectedItems.length} selected`;
    }
  } else {
    if (!props.modelValue) return "";

    const selected = props.items.find(
      (item) => item[props.valueField] === props.modelValue
    );
    // For creatable: show the raw value if no matching item found
    return selected ? selected[props.displayField] : (props.creatable ? props.modelValue : "");
  }
});

const selectedItemIcon = computed(() => {
  if (props.multiple || !props.modelValue || !props.showIcons) return "";

  const selected = props.items.find(
    (item) => item[props.valueField] === props.modelValue
  );
  return selected ? selected[props.iconField] : "";
});

const selectedItemColor = computed(() => {
  if (props.multiple || !props.modelValue || !props.showIcons) return "";

  const selected = props.items.find(
    (item) => item[props.valueField] === props.modelValue
  );
  return selected ? selected.color : "";
});

const selectedItemIconStyle = computed(() => {
  if (props.multiple || !props.modelValue || !props.showIcons) return "";

  const selected = props.items.find(
    (item) => item[props.valueField] === props.modelValue
  );
  return selected ? selected.iconStyle : "";
});

const selectItem = (item) => {
  // Skip disabled items
  if (item?.disabled) return;

  if (item === null) {
    // Handle "All" option
    if (props.multiple) {
      // Assign mode: tick everything, or clear when it is already all ticked.
      const next =
        props.allSelectsAll && !isAllSelected.value ? [...selectableValues.value] : [];
      emit("update:modelValue", next);
      emit(
        "select",
        next
          .map((value) => props.items.find((i) => i[props.valueField] === value))
          .filter(Boolean)
      );
    } else {
      emit("update:modelValue", "");
      emit("select", null);
    }
  } else {
    if (props.multiple) {
      const selectedValues = Array.isArray(props.modelValue)
        ? [...props.modelValue]
        : [];
      const itemValue = item[props.valueField];
      const index = selectedValues.indexOf(itemValue);

      if (index > -1) {
        // Remove item if already selected
        selectedValues.splice(index, 1);
      } else {
        // Add item if not selected
        selectedValues.push(itemValue);
      }

      emit("update:modelValue", selectedValues);
      emit(
        "select",
        selectedValues
          .map((value) =>
            props.items.find((i) => i[props.valueField] === value)
          )
          .filter(Boolean)
      );

      // Don't close dropdown in multi-select mode
    } else {
      emit("update:modelValue", item[props.valueField]);
      emit("select", item);
      isOpen.value = false;
    }
  }

  if (!props.multiple) {
    searchQuery.value = "";
  }
};

// Flat list of every selectable row in on-screen order — the "All" option,
// grouped items, then the "Create" row. Keyboard navigation walks this list
// so highlight index N always matches the Nth visible row.
const navigableItems = computed(() => {
  const list = [];
  if (props.showAllOption) list.push({ type: "all" });
  groupKeys.value.forEach((key) => {
    groupedItems.value[key].forEach((item) => list.push({ type: "item", item }));
  });
  if (creatableOption.value) list.push({ type: "create" });
  return list;
});

// item value -> flat navigation index, for highlight lookup in the template
const navIndexByValue = computed(() => {
  const map = {};
  navigableItems.value.forEach((entry, idx) => {
    if (entry.type === "item") map[entry.item[props.valueField]] = idx;
  });
  return map;
});

const creatableNavIndex = computed(() =>
  creatableOption.value ? navigableItems.value.length - 1 : -1
);

const moveHighlight = (delta) => {
  const count = navigableItems.value.length;
  if (!count) return;
  if (highlightedIndex.value < 0) {
    highlightedIndex.value = delta > 0 ? 0 : count - 1;
  } else {
    highlightedIndex.value = (highlightedIndex.value + delta + count) % count;
  }
};

const selectNavigable = (entry) => {
  if (!entry) return;
  if (entry.type === "all") selectItem(null);
  else if (entry.type === "create") selectCreatable();
  else selectItem(entry.item);
};

// Bound on the component root — keystrokes bubble up from the auto-focused
// search input (or the trigger button when search is hidden).
const handleKeydown = (event) => {
  switch (event.key) {
    case "ArrowDown":
    case "ArrowUp":
      event.preventDefault(); // keep the caret from moving in the search input
      if (!isOpen.value) {
        if (!props.disabled) {
          isOpen.value = true;
          emit("open");
        }
        return;
      }
      moveHighlight(event.key === "ArrowDown" ? 1 : -1);
      break;
    case "Enter": {
      if (!isOpen.value) return;
      event.preventDefault(); // don't submit surrounding forms
      // No highlight but a typed query: Enter picks the top *match* — never
      // the "All" row. Falls back to the create row when nothing matched.
      const entry = highlightedIndex.value >= 0
        ? navigableItems.value[highlightedIndex.value]
        : (searchQuery.value.trim()
            ? navigableItems.value.find((e) => e.type === "item") ??
              navigableItems.value.find((e) => e.type === "create") ??
              null
            : null);
      selectNavigable(entry);
      break;
    }
    case "Escape":
      if (isOpen.value) {
        // Close only the dropdown — don't let a parent modal's Escape
        // handler fire off the same keypress
        event.stopPropagation();
        isOpen.value = false;
      }
      break;
  }
};

const selectCreatable = () => {
  const value = searchQuery.value.trim();
  if (!value) return;
  emit("update:modelValue", value);
  emit("select", { [props.valueField]: value, [props.displayField]: value, _created: true });
  isOpen.value = false;
  searchQuery.value = "";
};
</script>

<style lang="scss" scoped>
.searchable-dropdown {
  display: block;
  width: 100%;
  position: relative;

  .select {
    width: 100%;

    &.no-arrow {
      &::after {
        display: none;
      }
    }

    .select-button {
      width: 100%;
      height: 2.5em;
      padding: calc(0.5em - 1px) calc(0.75em - 1px);
      background-color: white;
      border: 1px solid #dbdbdb;
      border-radius: 4px;
      color: #363636;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 1rem;
      line-height: 1.5;
      position: relative;
      text-align: left;

      &:hover {
        border-color: #b5b5b5;
      }

      &:focus {
        border-color: #485fc7;
        outline: none;
        box-shadow: 0 0 0 0.125em rgba(72, 95, 199, 0.25);
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
      
      // Support for truncation
      .has-text-truncate {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 1;
      }

      .icon.is-small {
        color: var(--bulma-primary, #485fc7);
        margin-left: 0.5rem;
        transition: transform 0.2s ease;
        height: 0.5em;
        width: 0.5em;
        position: absolute;
        right: 1em;
        top: 50%;
        transform-origin: center;
        transform: translateY(-50%);
      }
    }
  }

  &.is-active {
    .select-button .icon.is-small {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  .select-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 40; // Higher than most content but below modals
    margin-top: 0.25rem;
    
    /* Ensure dropdown appears above modal elements */
    .modal & {
      z-index: 50;
    }
  }

  .select-content {
    background: white;
    border-radius: 4px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 300px;
    overflow-y: auto;
    // overflow-y alone makes the x axis `auto` too, so anything a hair too wide
    // (a long name, a group header) added a stray horizontal scrollbar.
    overflow-x: hidden;
    position: relative;
    z-index: 40; // Ensure content is also at proper z-index
  }

  .search-item {
    padding: 0.5rem;
    border-bottom: 1px solid #dbdbdb;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;

    .input {
      padding-left: 2.25rem;
    }

    .icon {
      color: #7a7a7a;
    }
  }

  .list-header {
    padding: 0.5rem 1rem;
    background-color: #f5f5f5;
  }

  .list-item {
    display: block;
    padding: 0.5rem 1rem;
    cursor: pointer;
    
    // Support for truncation
    .has-text-truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }

    &:hover,
    &.is-highlighted {
      background-color: #f5f5f5;
    }

    &.is-selected {
      background-color: hsl(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        96%
      );
      border-left: 3px solid var(--bulma-primary, #485fc7);
    }

    .checkbox-label {
      cursor: pointer;
      width: 100%;

      input[type="checkbox"] {
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        width: 1rem;
        height: 1rem;
        border: 2px solid #dbdbdb;
        border-radius: 3px;
        background-color: white;
        position: relative;
        flex-shrink: 0;

        &:checked {
          background-color: var(--bulma-primary, #485fc7);
          border-color: var(--bulma-primary, #485fc7);

          &::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 1px;
            width: 4px;
            height: 8px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
        }

        &:hover {
          border-color: var(--bulma-primary, #485fc7);
        }
      }
    }
  }

  .creatable-item {
    border-top: 1px solid #dbdbdb;

    strong {
      color: var(--bulma-primary);
    }
  }

  .divider {
    margin: 0.5rem 0;
    background-color: #dbdbdb;
    border: none;
    height: 1px;
  }
}

// Ensure proper stacking context
.searchable-dropdown {
  position: relative;

  // When dropdown is active, ensure it's above siblings
  &.is-active {
    z-index: 30;
  }
}

// Dark mode styles - matches native select styling in dark sidebars
// Uses navy color palette: dark (#0f172a), medium (#1e293b), light (#334155)
.searchable-dropdown.is-dark {
  .select .select-button {
    background-color: #0f172a;
    border-color: #334155;
    color: white;

    &:hover {
      border-color: #475569;
    }

    &:focus {
      border-color: var(--bulma-primary);
      box-shadow: 0 0 0 0.125em rgba(var(--bulma-primary-rgb), 0.25);
    }

    .icon.is-small {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .select-content {
    background: #0f172a;
    border: 1px solid #334155;
  }

  .search-item {
    background: #0f172a;
    border-bottom-color: #334155;

    .input {
      background-color: #1e293b;
      border-color: #334155;
      color: white;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      &:focus {
        border-color: var(--bulma-primary);
        box-shadow: 0 0 0 0.125em rgba(var(--bulma-primary-rgb), 0.25);
      }
    }

    .icon {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .list-header {
    background-color: #1e293b;

    h6 {
      color: rgba(255, 255, 255, 0.6) !important;
    }
  }

  .list-item {
    color: white;

    &:hover,
    &.is-highlighted {
      background-color: #1e293b;
    }

    &.is-selected {
      background-color: rgba(var(--bulma-primary-rgb), 0.2);
      border-left-color: var(--bulma-primary);
    }

    &.has-text-grey {
      color: rgba(255, 255, 255, 0.5) !important;
    }

    .checkbox-label {
      input[type="checkbox"] {
        background-color: #1e293b;
        border-color: #334155;

        &:checked {
          background-color: var(--bulma-primary);
          border-color: var(--bulma-primary);
        }

        &:hover {
          border-color: var(--bulma-primary);
        }
      }
    }
  }

  .creatable-item {
    border-top-color: #334155;

    strong {
      color: var(--bulma-primary);
    }
  }

  .divider {
    background-color: #334155;
  }

  // No results text
  .has-text-grey {
    color: rgba(255, 255, 255, 0.5) !important;
  }
}
</style>
